/**
 * ============================================================================
 * DATA LAYER (DL): GESTIÓN DE PLAYLIST Y DINÁMICA "ARMA LA PLAYLIST" (Tile ID 46)
 * ============================================================================
 * Centraliza las peticiones HTTP hacia los endpoints del backend (.NET API).
 * Soporta vistas de aficionado, cabina de sonido DJ, overlays OBS y pantallas de estadio.
 */

const getApiBase = () => {
    const root = process.env.REACT_APP_API_URL || '';
    return root.endsWith('/') ? `${root}Playlist` : `${root}/Playlist`;
};

/**
 * Registra una sugerencia de aficionado desde la interfaz móvil.
 * @param {Object} suggestionData - { suggestionType, title, artistName, fanName, fanSessionId }
 */
export const SubmitSuggestion = async (suggestionData) => {
    try {
        const response = await fetch(`${getApiBase()}/suggest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(suggestionData)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'No fue posible registrar la sugerencia.',
                data: data
            };
        }

        return { success: true, data: data };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al enviar la sugerencia. Inténtalo nuevamente.'
        };
    }
};

/**
 * Obtiene la configuración pública activa para la pantalla móvil del aficionado.
 */
export const GetPublicConfig = async () => {
    try {
        const response = await fetch(`${getApiBase()}/config`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener la configuración pública.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar configuración pública.' };
    }
};

/**
 * Obtiene el listado completo de sugerencias registradas para moderación en cabina DJ.
 * @param {Object} options - { includeArchived: boolean, groupSimilar: boolean }
 */
export const GetSuggestions = async ({ includeArchived = false, groupSimilar = false } = {}) => {
    try {
        const url = `${getApiBase()}/suggestions?includeArchived=${includeArchived}&groupSimilar=${groupSimilar}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener sugerencias.' };
        }

        const data = await response.json();
        return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
        return { success: false, message: 'Error de conexión al obtener sugerencias.' };
    }
};

/**
 * Agrega una canción manual directamente por el operador o DJ.
 * @param {Object} manualData - { title, artistName, operatorName, suggestionType }
 */
export const CreateManualSong = async (manualData) => {
    try {
        const response = await fetch(`${getApiBase()}/manual-song`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(manualData)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al crear la canción manual.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión al crear canción manual.' };
    }
};

/**
 * Alterna el estado de favorita / agregada a la playlist activa.
 * @param {string} id - Identificador GUID de la sugerencia.
 * @param {boolean} [isFavorite] - Estado opcional a forzar.
 */
export const ToggleFavorite = async (id, isFavorite = null) => {
    try {
        const url = `${getApiBase()}/suggestions/${id}/toggle-favorite`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isFavorite !== null ? { isFavorite } : {})
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al alternar favorita.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al alternar favorita.' };
    }
};

/**
 * Alterna el estado de archivado de una sugerencia (oculta de la moderación principal).
 * @param {string} id - Identificador GUID de la sugerencia.
 * @param {boolean} [isArchive] - Estado opcional a forzar.
 */
export const ToggleArchive = async (id, isArchive = null) => {
    try {
        const url = `${getApiBase()}/suggestions/${id}/toggle-archive`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isArchive !== null ? { isArchive } : {})
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al alternar archivado.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al alternar archivado.' };
    }
};

/**
 * Actualiza los metadatos editables de una sugerencia (título, artista, aficionado).
 * @param {string} id - GUID de la sugerencia.
 * @param {Object} updateData - { title, artistName, fanName }
 */
export const UpdateSuggestion = async (id, updateData) => {
    try {
        const response = await fetch(`${getApiBase()}/suggestions/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al actualizar la sugerencia.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar la sugerencia.' };
    }
};

/**
 * Elimina lógicamente una sugerencia.
 * @param {string} id - GUID de la sugerencia.
 */
export const DeleteSuggestion = async (id) => {
    try {
        const response = await fetch(`${getApiBase()}/suggestions/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al eliminar la sugerencia.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al eliminar la sugerencia.' };
    }
};

/**
 * Obtiene la playlist activa de estadio (canciones con IsFavorite = true ordenadas por OrderIndex).
 */
export const GetActivePlaylist = async () => {
    try {
        const response = await fetch(`${getApiBase()}/active`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener la playlist activa.' };
        }

        const data = await response.json();
        return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
        return { success: false, message: 'Error de conexión al obtener playlist activa.' };
    }
};

/**
 * Reordena las canciones de la playlist activa según la lista secuencial de IDs proporcionada.
 * @param {Array<string>} orderedIds - Lista ordenada de GUIDs.
 */
export const ReorderPlaylist = async (orderedIds) => {
    try {
        const response = await fetch(`${getApiBase()}/reorder`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderedIds, orderedSuggestionIds: orderedIds })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al reordenar la playlist.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al reordenar la playlist.' };
    }
};

/**
 * Marca o desmarca la canción actual en reproducción ("Now Playing").
 * @param {string|null} songId - GUID de la canción o null para desmarcar.
 */
export const SetNowPlaying = async (songId) => {
    try {
        const response = await fetch(`${getApiBase()}/now-playing`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ songId: songId || null })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al cambiar la canción en reproducción.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar canción en reproducción.' };
    }
};

/**
 * Obtiene el estado actual de NowPlaying para visualización en pantalla o cabina.
 */
export const GetNowPlaying = async () => {
    try {
        const response = await fetch(`${getApiBase()}/now-playing`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener estado NowPlaying.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar NowPlaying.' };
    }
};

/**
 * Alterna el estado de reproducida (IsPlayed) de una canción.
 * @param {string} id - GUID de la sugerencia.
 * @param {boolean} [isPlayed] - Estado opcional.
 */
export const TogglePlayed = async (id, isPlayed = null) => {
    try {
        const url = `${getApiBase()}/suggestions/${id}/toggle-played`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isPlayed !== null ? { isPlayed } : {})
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al alternar estado reproducido.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al alternar estado reproducido.' };
    }
};

/**
 * Obtiene métricas y estadísticas consolidadas en tiempo real.
 */
export const GetMetrics = async () => {
    try {
        const response = await fetch(`${getApiBase()}/metrics`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener métricas.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión al obtener métricas.' };
    }
};

/**
 * Obtiene la configuración general y opciones de visualización de Playlist.
 */
export const GetSettings = async () => {
    try {
        const response = await fetch(`${getApiBase()}/settings`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            return { success: false, message: err.message || 'Error al obtener configuración.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión al obtener configuración.' };
    }
};

/**
 * Guarda y actualiza la configuración global de la dinámica.
 * @param {Object} settingsData - Configuración a guardar.
 */
export const UpdateSettings = async (settingsData) => {
    try {
        const response = await fetch(`${getApiBase()}/settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(settingsData)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al guardar la configuración.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de conexión al actualizar la configuración.' };
    }
};

/**
 * Pausa o reactiva de inmediato la recepción de sugerencias de aficionados.
 * @param {boolean} isActive - true para habilitar; false para pausar.
 */
export const TogglePause = async (isActive) => {
    try {
        const response = await fetch(`${getApiBase()}/toggle-pause`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isActive })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al pausar/reanudar sugerencias.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al pausar/reanudar sugerencias.' };
    }
};

/**
 * Cambia el modo de pantalla o proyección de estadio / overlay OBS.
 * @param {number|string} screenMode - Modo de pantalla (0: CallToAction, 1: NowPlaying, 2: Queue, etc.)
 */
export const SetScreenMode = async (screenMode) => {
    try {
        const response = await fetch(`${getApiBase()}/screen-mode`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ screenMode })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al cambiar modo de pantalla.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al cambiar modo de pantalla.' };
    }
};

/**
 * Proyecta la pantalla de Playlist en las pantallas gigantes del estadio.
 * @param {number|string} [screenMode=0] - Modo actual a proyectar (0: CallToAction, 1: NowPlaying, 2: Queue).
 */
export const ProyectarScreen = async (screenMode = 0) => {
    try {
        const response = await fetch(`${getApiBase()}/proyectar-screen`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ screenMode: Number(screenMode) })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return await SetScreenMode(screenMode);
        }

        return { success: true, data, message: data.message || 'Pantalla proyectada en el estadio.' };
    } catch (error) {
        return await SetScreenMode(screenMode);
    }
};

/**
 * Reinicia y purga los registros de sugerencias y votos mediante validación de contraseña maestra.
 * @param {string} confirmationPassword - Contraseña maestra de seguridad.
 */
export const ResetDynamic = async (confirmationPassword) => {
    try {
        const response = await fetch(`${getApiBase()}/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirmationPassword })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al reiniciar la dinámica.' };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al reiniciar la dinámica.' };
    }
};

/**
 * Descarga el archivo CSV con el registro consolidado de sugerencias y votos.
 */
export const ExportToExcel = async () => {
    try {
        const response = await fetch(`${getApiBase()}/export-excel`, {
            method: 'GET'
        });

        if (!response.ok) {
            return { success: false, message: 'Error al exportar los datos a archivo.' };
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `playlist_reporte_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error al procesar la descarga del archivo.' };
    }
};

/**
 * Sube y optimiza un asset multimedia con SkiaSharp para la dinámica de Playlist.
 * @param {string} assetType - Tipo de asset (overlay-1536, overlay-1920, screen-bg, qr-logo)
 * @param {File} file - Archivo binario de imagen
 * @param {boolean} [updateSettings=true] - Actualizar automáticamente en BD y notificar por WS
 */
export const UploadAsset = async (assetType, file, updateSettings = true) => {
    try {
        const formData = new FormData();
        formData.append('assetType', assetType);
        formData.append('file', file);
        formData.append('updateSettings', updateSettings);

        const response = await fetch(`${getApiBase()}/upload-asset`, {
            method: 'POST',
            body: formData
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Error al procesar y subir el asset multimedia.',
                data
            };
        }

        return { success: true, data };
    } catch (error) {
        return {
            success: false,
            message: 'Error de conexión al subir el archivo multimedia.'
        };
    }
};
